import { Dictionary } from './SpeechTextBuilder';
import { html as HTML_NS, pls as PLS_NS } from '../../../../common/src/ts/xmlns';
import { assign, filter, forEach, KeyVals, resolveURL } from '../../../../common/src/ts/utils';
import { isElement } from '../../../../common/src/ts/utils/dom';

function lexemeToDictionary(lexeme: Element,
                            dictionary: Dictionary,
                            prefered: Dictionary) {
  const graphemes =
    filter(lexeme.children, elem => elem.namespaceURI === PLS_NS && elem.tagName === 'grapheme');
  const aliases =
    filter(lexeme.children, elem => elem.namespaceURI === PLS_NS && elem.tagName === 'alias');
  for (const grapheme of graphemes) {
    const key = grapheme.textContent!;
    if (key === '') {
      continue;
    }
    if (key in prefered) {
      continue;
    }
    for (const alias of aliases) {
      const value = alias.textContent!;
      const prefer = alias.hasAttribute('prefer') && alias.getAttribute('prefer') === 'true';
      if (prefer) {
        prefered[key] = value;
        break;
      }
      if (!(key in dictionary)) {
        dictionary[key] = value;
      }
    }
  }
}

export function loadPLSFromDocument(document: Document): Readonly<Dictionary> {
  if (document.documentElement === null) {
    return {};
  }

  if (document.documentElement.namespaceURI !== PLS_NS
      && document.documentElement.tagName === 'lexicon') {
    return {};
  }
  if (document.getElementsByTagNameNS(HTML_NS, 'parsererror').length !== 0) {
    return {};
  }
  const lexemes = document.getElementsByTagNameNS(PLS_NS, 'lexeme');
  const dictionary: Dictionary = Object.create(null);
  const prefered: Dictionary = Object.create(null);
  forEach(lexemes, lexeme => lexemeToDictionary(lexeme, dictionary, prefered));
  return assign({}, dictionary, prefered);
}

export function loadPLSFromString(text: string): Readonly<Dictionary> {
  const parser = new DOMParser();
  let document;
  try {
    document = parser.parseFromString(text, 'application/xml');
  } catch (err) {
    // IE11では不正なXMLに対してSyntaxErrorを発生させる
    if (err instanceof SyntaxError) {
      return {};
    }
    throw err;
  }
  return loadPLSFromDocument(document);
}

const ponunciationLexiconCache: KeyVals<Promise<Readonly<Dictionary>>> = Object.create(null);

export function loadPLSFromURL(url: string): Promise<Readonly<Dictionary>> {
  if (!(url in ponunciationLexiconCache)) {
    ponunciationLexiconCache[url] = (async () => {
      return loadPLSFromString(await (await fetch(url)).text());
    })();
  }
  return  ponunciationLexiconCache[url];
}

function loadPLSIfRequired(element: Element, document: Document) {
  if (
    document.location !== null
    && element.namespaceURI === HTML_NS
    && element.tagName === 'link'
    && element.hasAttribute('rel')
    && element.getAttribute('rel') === 'pronunciation'
    && element.hasAttribute('href')
  ) {
    loadPLSFromURL(resolveURL(element.getAttribute('href')!, document.location.href));
  }
}

export function registerPLSLoader(document: Document) {
  const observer = new MutationObserver(
    (mutations: MutationRecord[]) => {
      for (const mutation of mutations) {
        switch (mutation.type) {
          case 'childList':
            forEach(mutation.addedNodes, (node) => {
              if (isElement(node)) {
                loadPLSIfRequired(node as Element, document);
              }
            });
            break;
          case 'attributes':
            if (isElement(mutation.target)) {
              loadPLSIfRequired(mutation.target, document);
            }
            break;
        }
      }
    },
  );
  observer.observe(document, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['rel', 'href'],
  });
  const links = document.querySelectorAll('link[rel=pronunciation]');
  forEach(links, (link: Element) => loadPLSIfRequired(link, document));
}
